/**
 * Web Push Service
 * 
 * Handles subscription to web push notifications
 * 
 * IMPORTANT: Uses getCachedUserId() from AuthContext.
 * DO NOT call supabase.auth.getUser() here — it causes auth loops!
 */

import { supabase } from '@/integrations/supabase/client';
import { getCachedUserId } from '@/contexts/AuthContext';
import { logger } from '@/lib/services/logger';

// VAPID public key - must match the one in edge function
const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 
  // Fallback to fetch from edge function if not in env
  null;

interface PushSubscriptionState {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  subscribed: boolean;
}

/**
 * Check if browser supports push notifications
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

/**
 * Get current push state
 */
export async function getPushState(): Promise<PushSubscriptionState> {
  if (!isPushSupported()) {
    return { supported: false, permission: 'unsupported', subscribed: false };
  }

  const permission = Notification.permission;
  let subscribed = false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    subscribed = !!subscription;
  } catch (err) {
    logger.warn('Error checking WebPush subscription', {
      context: { feature: 'webpush', function_name: 'getPushState' },
      error: err,
      error_code: 'webpush_state_check_failed',
      error_type: 'unexpected',
    });
  }

  return { supported: true, permission, subscribed };
}

/**
 * Register service worker
 * Attempts to reuse existing registration to avoid duplicate registrations
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[WebPush] Service workers not supported');
    return null;
  }

  try {
    // First, try to get existing registration (e.g., from main.tsx)
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) {
      console.log('[WebPush] Reusing existing SW registration:', existing.scope);
      return existing;
    }
    
    // No existing registration, register new one
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    console.log('[WebPush] Service worker registered:', registration.scope);
    return registration;
  } catch (err) {
    // Use warn instead of error - SW failures are expected in some environments
    logger.warn('Service worker registration failed', {
      context: { feature: 'webpush', function_name: 'registerServiceWorker' },
      error: err,
      error_code: 'sw_registration_failed',
    });
    return null;
  }
}

/**
 * Get VAPID public key from edge function
 */
async function getVapidPublicKey(): Promise<string | null> {
  if (VAPID_PUBLIC_KEY) return VAPID_PUBLIC_KEY;

  try {
    const { data, error } = await supabase.functions.invoke('webpush-config', {
      method: 'GET'
    });

    if (error) throw error;
    return data?.vapid_public_key || null;
  } catch (err) {
    logger.error('Failed to get VAPID key', {
      context: { feature: 'webpush', function_name: 'getVapidPublicKey' },
      error: err,
      error_code: 'vapid_key_fetch_failed',
      error_type: 'network',
    });
    return null;
  }
}

/**
 * Validate VAPID public key format
 */
function isValidVapidKey(key: string): boolean {
  // VAPID public key should be ~87 characters (base64url encoded 65-byte ECDSA P-256 key)
  if (!key || key.length < 80 || key.length > 100) {
    logger.error('Invalid VAPID key length', {
      context: { feature: 'webpush', function_name: 'isValidVapidKey' },
      error_code: 'vapid_key_invalid_length',
      error_type: 'security',
    });
    return false;
  }
  // Check for valid base64url characters
  if (!/^[A-Za-z0-9_-]+$/.test(key)) {
    logger.error('VAPID key contains invalid characters', {
      context: { feature: 'webpush', function_name: 'isValidVapidKey' },
      error_code: 'vapid_key_invalid_chars',
      error_type: 'security',
    });
    return false;
  }
  return true;
}

/**
 * Convert VAPID key to Uint8Array for subscription
 */
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  // Validate VAPID key before processing
  if (!isValidVapidKey(base64String)) {
    throw new Error(`Неверный формат VAPID ключа (длина: ${base64String?.length || 0}). Проверьте настройки.`);
  }

  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  try {
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray.buffer;
  } catch (e) {
    logger.error('Failed to decode VAPID key', {
      context: { feature: 'webpush', function_name: 'urlBase64ToUint8Array' },
      error: e,
      error_code: 'vapid_key_decode_failed',
      error_type: 'security',
    });
    throw new Error('Не удалось декодировать VAPID ключ. Проверьте настройки.');
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(): Promise<{ success: boolean; error?: string }> {
  if (!isPushSupported()) {
    return { success: false, error: 'Push notifications not supported' };
  }

  try {
    // Request permission (must be user-initiated)
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Permission denied' };
    }

    // Get VAPID key
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      return { success: false, error: 'Failed to get VAPID key' };
    }

    // Ensure service worker is registered and ACTIVE before subscribing
    const registered = await registerServiceWorker();
    if (!registered) {
      return { success: false, error: 'Service worker registration failed' };
    }

    const readyRegistration = await navigator.serviceWorker.ready;

    // Reuse existing subscription if present
    let subscription = await readyRegistration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await readyRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
    }

    // Save subscription to backend using cached user ID
    const userId = getCachedUserId();
    if (!userId) {
      return { success: false, error: 'Not authenticated' };
    }

    // Store subscription in user_channel_endpoints
    const subscriptionJson = subscription.toJSON();
    const jsonData = JSON.parse(JSON.stringify(subscriptionJson));

    // First, try to find existing endpoint
    const { data: existing } = await supabase
      .from('user_channel_endpoints')
      .select('id')
      .eq('user_id', userId)
      .eq('channel', 'web_push')
      .is('deleted_at', null)
      .maybeSingle();

    if (existing) {
      // Update existing
      const { error: updateError } = await supabase
        .from('user_channel_endpoints')
        .update({
          address: subscription.endpoint,
          meta_json: jsonData,
          is_verified: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing.id);

      if (updateError) {
        logger.error('Failed to update WebPush subscription', {
          context: { feature: 'webpush', function_name: 'subscribeToPush' },
          error: updateError,
          error_code: 'webpush_subscription_update_failed',
          error_type: 'network',
        });
        return { success: false, error: 'Failed to save subscription' };
      }
    } else {
      // Insert new
      const { error: insertError } = await supabase
        .from('user_channel_endpoints')
        .insert([
          {
            user_id: userId,
            channel: 'web_push',
            address: subscription.endpoint,
            meta_json: jsonData,
            is_verified: true,
          },
        ]);

      if (insertError) {
        logger.error('Failed to save WebPush subscription', {
          context: { feature: 'webpush', function_name: 'subscribeToPush' },
          error: insertError,
          error_code: 'webpush_subscription_insert_failed',
          error_type: 'network',
        });
        return { success: false, error: 'Failed to save subscription' };
      }
    }

    return { success: true };
  } catch (err) {
    logger.error('WebPush subscribe error', {
      context: { feature: 'webpush', function_name: 'subscribeToPush' },
      error: err,
      error_code: 'webpush_subscribe_error',
      error_type: 'unexpected',
    });
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}


/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<{ success: boolean; error?: string }> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
    }

    // Remove from database using cached user ID
    const userId = getCachedUserId();
    if (userId) {
      await supabase
        .from('user_channel_endpoints')
        .update({ deleted_at: new Date().toISOString() })
        .eq('user_id', userId)
        .eq('channel', 'web_push');
    }

    return { success: true };
  } catch (err) {
    logger.error('WebPush unsubscribe error', {
      context: { feature: 'webpush', function_name: 'unsubscribeFromPush' },
      error: err,
      error_code: 'webpush_unsubscribe_error',
      error_type: 'unexpected',
    });
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

/**
 * Send test push notification (for debugging)
 */
export async function sendTestPush(): Promise<{ success: boolean; error?: string }> {
  try {
    const { data, error } = await supabase.functions.invoke('webpush-send', {
      body: {
        test: true,
        title: 'Тестовое уведомление',
        body: 'Web Push работает!'
      }
    });

    if (error) throw error;
    return { success: data?.ok || false };
  } catch (err) {
    logger.error('WebPush test push error', {
      context: { feature: 'webpush', function_name: 'sendTestPush' },
      error: err,
      error_code: 'webpush_test_error',
      error_type: 'network',
    });
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
  }
}

export const webPushService = {
  isPushSupported,
  getPushState,
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush
};

export default webPushService;
